export type TranslationKey =
  | 'pleaseFillRequiredFields'
  | 'cardDeclined'
  | 'unableToVerifyPayment'
  | 'verifyingPaymentMethod'
  | 'paymentMethodAddedSuccessfully'
  | 'addCard'
  | 'processing'
  | 'countryRequired'
  | 'cardNumber'
  | 'expiryMonth'
  | 'cvv'
  | 'expiryYear'
  | 'firstName'
  | 'lastName'
  | 'country'
  | 'postalCode'
  | 'placeholderMM'
  | 'placeholderYYYY'
  | 'required'
  | 'autoRenewEnrollmentFailed'
  | 'unknownError'
  | 'tokenizationFailed'
  | 'pleaseIncludeInfoMessage'
  | 'authenticationError';
  
export type Translations = {
  [key in TranslationKey]: string;
};

export const translations: Record<string, Translations> = {
  'en-US': {
    pleaseFillRequiredFields: 'Please fill required fields',
    cardDeclined: 'Card declined: Your card was declined by the payment processor',
    unableToVerifyPayment: 'Unable to verify payment after 3 attempts. The form has been reset - please re-enter your card information.',
    verifyingPaymentMethod: 'Verifying payment method...',
    paymentMethodAddedSuccessfully: 'Payment method added successfully!',
    addCard: 'Add Card',
    processing: 'Processing...',
    countryRequired: 'Country required (expected USA or CAN)',
    cardNumber: 'Card Number',
    expiryMonth: 'Expiry Month (MM)',
    cvv: 'CVV',
    expiryYear: 'Expiry Year (YYYY)',
    firstName: 'First Name',
    lastName: 'Last Name',
    country: 'Country',
    postalCode: 'Postal Code',
    placeholderMM: 'MM',
    placeholderYYYY: 'YYYY',
    required: 'Required',
    autoRenewEnrollmentFailed: 'Auto-renew enrollment failed',
    unknownError: 'Unknown error',
    tokenizationFailed: 'Unable to process card information. Please try again.',
    pleaseIncludeInfoMessage: 'Please include the following error message in any reported incidents:',
    authenticationError: 'There was an error processing your request',
  },
  'fr-CA': {
    pleaseFillRequiredFields: 'Veuillez remplir les champs obligatoires',
    cardDeclined: 'Carte refusée : Votre carte a été refusée par le processeur de paiement',
    unableToVerifyPayment: 'Impossible de vérifier le paiement après trois tentatives. Le formulaire a été réinitialisé, veuillez saisir les renseignements de votre carte à nouveau.',
    verifyingPaymentMethod: 'Vérification du mode de paiement…',
    paymentMethodAddedSuccessfully: 'Mode de paiement ajouté avec succès!',
    addCard: 'Ajouter une carte',
    processing: 'Traitement en cours…',
    countryRequired: 'Pays requis (doit être É.-U. ou CA)',
    cardNumber: 'Numéro de la carte',
    expiryMonth: 'Mois d\'expiration (MM)',
    cvv: 'Code CVV',
    expiryYear: 'Année d\'expiration (AAAA)',
    firstName: 'Prénom',
    lastName: 'Nom de famille',
    country: 'Pays',
    postalCode: 'Code postal',
    placeholderMM: 'MM',
    placeholderYYYY: 'AAAA',
    required: 'Requis',
    autoRenewEnrollmentFailed: 'Le renouvellement automatique a échoué',
    unknownError: 'Erreur inconnue',
    tokenizationFailed: 'Nous ne sommes pas en mesure de traiter les renseignements de la carte',
    pleaseIncludeInfoMessage: 'Veuillez inclure ce message d’erreur dans tous les incidents signalés',
    authenticationError: 'Une erreur est survenue lors du traitement de votre requête',
  },
};

// Helper function to get translation
export const getTranslation = (locale: string, key: TranslationKey): string => {
  const localeTranslations = translations[locale] || translations['en-US'];
  return localeTranslations[key] || translations['en-US'][key];
};

// Hook to easily get translation function
import { useLocale } from '../context/LocaleContext';

export const useTranslation = () => {
  const { locale } = useLocale();
  
  const t = (key: TranslationKey): string => {
    return getTranslation(locale, key);
  };
  
  return { t, locale };
};
